import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';

// Only log in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Debug logger function
const debugLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[AuthContext] ${message}`);
    if (data) console.log(data);
  }
};

// Define the User type
export interface User {
  id: string;
  displayName: string;
  email?: string;
  images?: Array<{ url: string }>;
  country?: string;
  product?: string;
}

// Define the context type
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  getAccessToken: async () => null,
});

// Create a provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        console.log('AuthContext: Checking authentication status');
        
        const token = await authService.getAccessToken();
        console.log('AuthContext: Token available:', !!token);
        
        if (token) {
          // Only fetch profile if we don't already have a user or if we need to refresh user data
          if (!user) {
            console.log('AuthContext: Fetching user profile');
            const userProfile = await authService.fetchUserProfile();
            console.log('AuthContext: User profile fetched:', !!userProfile);
            
            if (userProfile) {
              console.log('AuthContext: Setting user state with profile');
              setUser(userProfile);
            } else {
              console.error('AuthContext: User profile is null despite having a token');
              authService.logout();
              setUser(null);
            }
          }
        } else {
          console.log('AuthContext: No token available, user not authenticated');
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        // Clear any invalid tokens
        authService.logout();
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log('AuthContext: Authentication check completed');
      }
    };

    checkAuth();
    
    // Re-run auth check only when token might be expiring
    const intervalId = setInterval(() => {
      // Only run the check if we're close to token expiration
      const tokenExpiresAt = localStorage.getItem('spotify_token_expiration');
      if (tokenExpiresAt) {
        const expiresAt = parseInt(tokenExpiresAt, 10);
        const now = Date.now();
        // Only check if token expires in less than 5 minutes
        if (expiresAt - now < 300000) {
          console.log('AuthContext: Token expiring soon, running auth check');
          checkAuth();
        }
      }
    }, 60000); // Still check every minute, but only run if needed
    
    return () => {
      clearInterval(intervalId);
    };
  }, [user]); // Add user as dependency to prevent unnecessary effect runs

  const login = () => {
    authService.login();
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const getAccessToken = async (): Promise<string | null> => {
    return authService.getAccessToken();
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create a hook to use the auth context
export const useAuth = () => useContext(AuthContext);
